import {
  ADD_COLOR_ID,
  MANAGE_ID,
  PLACEHOLDER_ID,
  useProjectColorMenu,
} from '@components/ArtBoard/ArtboardToolbar'
import type { ID } from '@octopus-do/octopus-project-core'
import type {
  NewTagColor,
  PickerTag,
  TagMove,
} from '@octopus-do/octopus-storybook'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import {
  addNodeToTag,
  changeTagOrder,
  deleteNodeFromTag,
  deleteTag,
  thunkAddTag,
  updateTagColor,
  updateTagTitle,
} from '@store/reducers/tags/actions'
import type { AppState } from '@store/types'
import {
  setSelectedNodeLink,
  setShowTagDropdown,
} from '@storeOld/reducers/canvas/canvas'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'

type AppDispatch = ThunkDispatch<AppState, unknown, Action>

// The tag whose color menu picked "+": the added color goes to it; NEW_TAG is
// the tag about to be created from the field.
const NEW_TAG = null

// The color menus: a color id recolors, "+" opens the picker for that tag under
// the popover, Manage closes the popover on the way to the color library, a
// grey slot sells the plan.
const useTagColorMenu = (defaultColorId: ID, close: () => void) => {
  const dispatch = useDispatch<AppDispatch>()
  const tags = useSelector(migrationProjectSelector.getProjectTags)
  const [pending, setPending] = useState<ID | typeof NEW_TAG>(NEW_TAG)
  // The color of the tag to be created, picked in the menu next to the field.
  const [newColorId, setNewColorId] = useState<ID | null>(null)
  const newTagColorId = newColorId ?? defaultColorId
  const pendingColorId =
    tags.find((tag) => tag.id === pending)?.colorId ?? newTagColorId

  const recolor = (id: ID | typeof NEW_TAG, colorId: ID) => {
    if (id === NEW_TAG) {
      setNewColorId(colorId)
    } else {
      dispatch(updateTagColor({ id, colorId }))
    }
  }

  const menu = useProjectColorMenu(
    pendingColorId,
    (colorId) => recolor(pending, colorId),
    close
  )

  const onColorMenu = (id: ID | typeof NEW_TAG, [key]: string[]) => {
    if (key === ADD_COLOR_ID) {
      setPending(id)
      menu.onAdd()
    } else if (key === MANAGE_ID) {
      menu.onManageClick()
    } else if (key === PLACEHOLDER_ID) {
      menu.onUpgrade()
    } else {
      recolor(id, key)
    }
  }

  return {
    colorHex: menu.colorHex,
    newTagColorId,
    onColorMenu,
    onNewTagColorMenu: (keys: string[]) => onColorMenu(NEW_TAG, keys),
    picker: menu.picker,
    resetNewTagColor: () => setNewColorId(null),
  }
}

// The popover state: `nodeId` decides the checkers, `nodes` get the toggled
// tag (the selection from the toolbar, the page itself from its chips).
export const useTagsPopover = (nodeId: ID, nodes: ID[], close: () => void) => {
  const dispatch = useDispatch<AppDispatch>()
  const tags = useSelector(migrationProjectSelector.getProjectTags)
  const colors = useSelector(migrationProjectSelector.getProjectColors)
  const defaultColorId = useSelector(
    migrationProjectSelector.getProjectDefaultColorId
  )
  const colorMenu = useTagColorMenu(defaultColorId, close)

  const hexOf = (colorId: ID) =>
    colors.find((color) => color.id === colorId)?.hexCode ?? ''

  const picker: PickerTag[] = tags.map((tag) => ({
    checked: tag.nodes.includes(nodeId),
    color: hexOf(tag.colorId),
    colorId: tag.colorId,
    id: tag.id,
    title: tag.title,
  }))

  const onToggle = (id: ID) => {
    sendGtmEvent(
      GtmTriggerName.TAGS_ADD_TO_PAGE,
      'Click to add/remove tag to/from page'
    )
    const checked = picker.some((tag) => tag.id === id && tag.checked)
    dispatch(checked ? deleteNodeFromTag(id, nodes) : addNodeToTag(id, nodes))
  }

  // A new tag goes onto the page right away, in the color picked next to the field.
  const onCreate = (title: string) => {
    sendGtmEvent(GtmTriggerName.TAGS_ADD_NEW, 'Add new tag')
    const id = dispatch(thunkAddTag(title, colorMenu.newTagColorId))
    dispatch(addNodeToTag(id, nodes))
    colorMenu.resetNewTagColor()
  }

  const newTagColor: NewTagColor = {
    color: hexOf(colorMenu.newTagColorId),
    colorId: colorMenu.newTagColorId,
  }

  const onMove = (id: ID, direction: TagMove) => {
    const index = tags.findIndex((tag) => tag.id === id)
    const next = direction === 'up' ? index - 1 : index + 1
    dispatch(changeTagOrder({ id, index: next }))
  }

  const popover = {
    newTagColor,
    onColorMenu: colorMenu.onColorMenu,
    onCreate,
    onDelete: (id: ID) => dispatch(deleteTag(id)),
    onMove,
    onNewTagColorMenu: colorMenu.onNewTagColorMenu,
    onRename: (id: ID, title: string) =>
      dispatch(updateTagTitle({ id, title })),
    onToggle,
    tags: picker,
  }

  return { colorHex: colorMenu.colorHex, picker: colorMenu.picker, popover }
}

// While the popover is open the page shortcuts stay out of its fields and
// Delete does not remove the page (the delete shortcut checks the tags flag).
export const useTagsGuard = (nodeId: ID, active: boolean) => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(setSelectedNodeLink(active ? nodeId : null))
    dispatch(setShowTagDropdown(active ? nodeId : null))

    return () => {
      dispatch(setSelectedNodeLink(null))
      dispatch(setShowTagDropdown(null))
    }
  }, [active])
}
